// Canonical credit tier and mode constants.
// All credit-related code should import from here instead of hardcoding strings.

export const CREDIT_TIERS = ["starter", "pro"] as const;
export type CreditTier = (typeof CREDIT_TIERS)[number];

export const CREDIT_MODES = ["one_time", "subscription"] as const;
export type CreditMode = (typeof CREDIT_MODES)[number];

/** Number of credits granted per tier (same for one-time and subscription) */
export const CREDIT_AMOUNT_MAP: Record<CreditTier, number> = {
  starter: 10_000,
  pro: 100_000,
};

/** Dollar price per tier (numeric, for calculations and Pay buttons) */
export const CREDIT_PRICE_MAP: Record<CreditTier, number> = {
  starter: 5,
  pro: 50,
};

/** Formatted price strings for display */
export const CREDIT_PRICE_FORMATTED_MAP: Record<CreditTier, string> = {
  starter: "USD 5.00",
  pro: "USD 50.00",
};

/**
 * Updated-pricing generation (behind the `updated_credits_pricing` Statsig
 * gate — see `apps/web/lib/credits/updated-credits-pricing-gate.ts`):
 * $20 per 10,000 credits (starter), $200 per 100,000 credits (pro). The
 * original maps above are untouched — both server checkout routes read them
 * for the gate-off arm, and grandfathered purchasers keep seeing them.
 */
export const UPDATED_CREDIT_AMOUNT_MAP: Record<CreditTier, number> = {
  starter: 10_000,
  pro: 100_000,
};

export const UPDATED_CREDIT_PRICE_MAP: Record<CreditTier, number> = {
  starter: 20,
  pro: 200,
};

export const UPDATED_CREDIT_PRICE_FORMATTED_MAP: Record<CreditTier, string> = {
  starter: "USD 20.00",
  pro: "USD 200.00",
};

export interface CreditPricing {
  amounts: Record<CreditTier, number>;
  prices: Record<CreditTier, number>;
  pricesFormatted: Record<CreditTier, string>;
}

/**
 * Single seam for resolving credit pricing by generation. `updated` is the
 * resolved `updated_credits_pricing` gate value — callers never branch on
 * the two map families directly, so a gate-off caller and a gate-on caller
 * can never accidentally mix maps from different generations.
 */
export function getCreditPricing(updated: boolean): CreditPricing {
  return updated
    ? {
        amounts: UPDATED_CREDIT_AMOUNT_MAP,
        prices: UPDATED_CREDIT_PRICE_MAP,
        pricesFormatted: UPDATED_CREDIT_PRICE_FORMATTED_MAP,
      }
    : {
        amounts: CREDIT_AMOUNT_MAP,
        prices: CREDIT_PRICE_MAP,
        pricesFormatted: CREDIT_PRICE_FORMATTED_MAP,
      };
}

export interface MessagingRates {
  email: number;
  sms: number;
}

/**
 * Send-time credit rates by pricing generation — the burn-side sibling of
 * `getCreditPricing`, kept separate (D12) because its `{ email, sms }` shape
 * is what `estimateMessagingCost`'s `rates` param and the southpaw create-job
 * debits consume. Email stays 1 credit in both generations; SMS drops from
 * 20 to 10 on the updated arm (D6 — buy price and burn rate flip together).
 */
export const LEGACY_MESSAGING_RATES: MessagingRates = Object.freeze({
  email: 1,
  sms: 20,
});
export const UPDATED_MESSAGING_RATES: MessagingRates = Object.freeze({
  email: 1,
  sms: 10,
});

export function getMessagingRates(updated: boolean): MessagingRates {
  return updated ? UPDATED_MESSAGING_RATES : LEGACY_MESSAGING_RATES;
}
